import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/base/Button";
import SEO from "../../../../components/base/SEO";
import Footer from "../../../../components/feature/Footer";
import Header from "../../../../components/feature/Header";

const workflowSteps = [
  {
    step: "01",
    title: "查詢與報價",
    before: "客戶用 WhatsApp、電話或 email 查詢包車，業務再用 Excel 或舊報價格式回覆。",
    erp: "在 ERP 建立客戶、行程、日期、車型、服務時段和報價版本，並先按當日訂單和車輛佔用情況判斷是否可接。",
  },
  {
    step: "02",
    title: "當日訂單與車輛可用情況",
    before: "幾個銷售同時接查詢時，要翻不同 Excel、訊息或排車表，才知道當日哪些車已被預留、哪些車仍可安排。",
    erp: "銷售和車務可用同一視角查看指定日期的訂單、車型需求、已安排車輛和空置車輛，減少重覆承諾或漏接機會。",
  },
  {
    step: "03",
    title: "訂單與派車",
    before: "確認訂單後，車務同事再把資料搬到另一份排車表，容易漏改時間、地點或客戶要求。",
    erp: "訂單資料直接成為派車依據，車務可按日期、車型、司機、車牌和行程狀態安排資源。",
  },
  {
    step: "04",
    title: "服務完成與異常紀錄",
    before: "完成服務後靠司機、前線或客戶訊息確認，有加鐘、改路線或等待費時容易漏記。",
    erp: "在同一張訂單記錄完成狀態、額外費用、備註和附件，讓財務開票前有完整依據。",
  },
  {
    step: "05",
    title: "發票、月結、預存款與扣數",
    before: "熟客如旅行社或學校，很多時不是逐張單找數，而是先入一筆錢，再由財務用人手逐張扣減和核對餘額。",
    erp: "ERP 可記錄客戶預存款、每張包車單扣數、剩餘餘額、月結發票和付款紀錄，讓財務不用另外用 Excel 追客戶餘額。",
  },
] as const;

const painPoints = [
  "同一張包車訂單在業務、車務和財務手上有不同版本",
  "幾個銷售同時接單時，很難即時知道當日哪些車輛已被預留、哪些仍然空置可用",
  "報價確認後仍要人手重新輸入到訂單、排車表和發票",
  "臨時改時間、改地點、加鐘或取消服務時，內部未必同步",
  "熟客先入一筆錢再慢慢扣數時，客戶餘額和每張單扣款紀錄容易分散在 Excel",
  "月結客戶要逐張訂單核對服務日期、reference、金額和付款",
  "管理層很難即時知道本月完成訂單、未開票、未收款和高峰期接單情況",
] as const;

const erpModules = [
  {
    title: "銷售管理",
    text: "處理查詢、報價、客戶價格、報價批核和報價轉訂單，並讓銷售在回覆前先看到相關日期的訂單和車輛可用情況。",
  },
  {
    title: "訂單管理",
    text: "集中記錄包車日期、行程、車型、客戶要求、狀態和完成紀錄，讓每個部門看同一張單。",
  },
  {
    title: "車務 / 資源安排",
    text: "以訂單資料支援派車、司機、車牌、時段和車輛佔用狀態安排，避免排車表與客戶訂單脫節。",
  },
  {
    title: "財務會計",
    text: "把完成訂單轉成應收、發票、月結和付款追蹤，同時支援熟客預存款、按訂單扣數和客戶餘額管理。",
  },
  {
    title: "客戶 CRM",
    text: "保留學校、旅行社、企業客戶、長期月結客戶的聯絡人、付款條件、預存款安排和歷史訂單。",
  },
  {
    title: "報表分析",
    text: "按客戶、月份、路線、車型和訂單狀態查看收入、未收款、預存款餘額和高峰期接單情況。",
  },
] as const;

const outcomes = [
  {
    metric: "減少重覆輸入",
    detail: "報價、訂單、派車和發票使用同一組核心資料，減少每個部門重新打一次資料。",
  },
  {
    metric: "訂單狀態更清楚",
    detail: "由查詢、已報價、已確認、已安排、已完成、已開票到已收款，可以用同一套狀態追蹤。",
  },
  {
    metric: "車輛可用情況更清楚",
    detail: "銷售接查詢時可先看指定日期和時段的訂單與車輛佔用情況，減少重覆預留或來回問車務。",
  },
  {
    metric: "月結更易核對",
    detail: "財務可按客戶和月份整理完成訂單，不需要每次由零開始翻查訊息和 Excel。",
  },
  {
    metric: "熟客扣數更清楚",
    detail: "旅行社、學校等熟客先入錢後，每張包車單可以扣減預存款並保留餘額紀錄，減少人手對數。",
  },
  {
    metric: "管理層更早看到風險",
    detail: "未完成訂單、未開票、未收款和高峰期資源壓力，可以在日常營運中更早被看見。",
  },
] as const;

const faqItems = [
  {
    q: "旅遊巴或包車公司導入 ERP 是否一定要先做完整車隊系統？",
    a: "不一定。很多公司更適合先由報價、訂單、派車資料和月結對數開始，先把同一張單的資料集中，再逐步加入更深入的車務或司機流程。",
  },
  {
    q: "如果公司仍然主要用 WhatsApp 接單，可以導入 ERP 嗎？",
    a: "可以。ERP 不一定要取代所有溝通工具，但確認後的客戶資料、服務日期、行程、車型、金額和狀態應該回到系統，否則財務和管理層仍然要靠人手追資料。",
  },
  {
    q: "這類 ERP 第一階段最應該處理什麼？",
    a: "通常先處理報價轉訂單、訂單轉派車、完成服務轉應收，以及月結和熟客扣數。這幾段一旦接起來，前線、車務和財務會更容易用同一套資料合作。",
  },
  {
    q: "如果旅行社或學校會先入一筆錢，再慢慢扣包車費，可以處理嗎？",
    a: "可以。ERP 可以為熟客記錄預存款，之後每張包車單按實際金額扣數，保留扣款紀錄和剩餘餘額。財務就不用另外開 Excel 追每個客戶還剩多少錢。",
  },
  {
    q: "AUTOCHAIN 會否要求公司完全改變現有 SOP？",
    a: "我們會先理解公司現有接單、派車和對數做法，再判斷哪些做法可以保留，哪些位置需要標準化。重點是讓 ERP 承接實際營運，而不是叫團隊硬遷就功能表。",
  },
] as const;

export default function ERPTourBusCaseStudyPage() {
  const { t } = useTranslation();
  const isZh = t("nav.language") === "中";
  const pageTitle =
    "旅遊巴 / 包車公司 ERP 案例 | 報價、派車、月結與熟客扣數 | AUTOCHAIN";
  const pageDescription =
    "AUTOCHAIN ERP 案例：旅遊巴及包車公司如何把客戶報價、包車訂單、車務安排、服務完成、發票、月結、熟客預存款和扣數整理到同一套 ERP 流程。";

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const openWhatsApp = (zhMessage: string, enMessage: string) => {
    const message = encodeURIComponent(isZh ? zhMessage : enMessage);
    window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#f7f5ef] text-slate-900">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords="旅遊巴 ERP,包車公司 ERP,車隊 ERP,派車系統,月結對數,熟客扣數,預存款管理,ERP 案例,香港 ERP 案例,中小企 ERP"
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogImage="https://autochain.com.hk/erp-og-image.png"
        canonicalUrl="/erp/case-studies/tour-bus"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: pageTitle,
            description: pageDescription,
            image: "https://autochain.com.hk/erp-og-image.png",
            datePublished: "2026-06-22",
            dateModified: "2026-06-22",
            inLanguage: "zh-Hant-HK",
            author: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
            },
            publisher: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
              logo: {
                "@type": "ImageObject",
                url: "https://autochain.com.hk/logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": "https://autochain.com.hk/erp/case-studies/tour-bus",
            },
            articleSection: "ERP 案例分享",
            keywords: [
              "旅遊巴 ERP",
              "包車公司 ERP",
              "派車流程",
              "月結對數",
              "熟客預存款",
              "按單扣數",
              "香港中小企 ERP",
            ],
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "ERP 企業管理系統",
                item: "https://autochain.com.hk/erp",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "ERP 案例分享",
                item: "https://autochain.com.hk/erp/case-studies",
              },
              {
                "@type": "ListItem",
                position: 4,
                name: "旅遊巴 / 包車公司 ERP 案例",
                item: "https://autochain.com.hk/erp/case-studies/tour-bus",
              },
            ],
          }),
        }}
      />

      <Header />

      <main>
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-teal-700">
                首頁
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/erp" className="hover:text-teal-700">
                ERP
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/erp/case-studies" className="hover:text-teal-700">
                案例分享
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="font-medium text-slate-900">
                旅遊巴 / 包車公司
              </span>
            </nav>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8 lg:py-20">
            <div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700">
                <i className="ri-bus-2-line text-base"></i>
                ERP 案例 01
              </div>
              <h1 className="mt-6 text-4xl font-bold leading-tight text-slate-950 md:text-6xl">
                旅遊巴 / 包車公司 ERP 案例：
                <span className="block text-teal-700">
                  由報價、查車、派車到熟客扣數放回同一流程
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
                這類公司最常見的問題，不是完全沒有流程，而是每個部門都有自己的流程。
                業務有報價表，車務有排車表，財務有月結表。當幾個銷售同時接查詢時，連當日哪些車輛仍然空置可用，也要靠人手在幾份資料之間核對。
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-7 py-4 text-base"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我看過旅遊巴 / 包車公司 ERP 案例，想了解我們的報價、派車、月結和熟客扣數流程可以怎樣整理。",
                      "Hello! I reviewed the tour bus ERP case study and would like to understand how our quotation, dispatch, monthly billing and customer balance deduction workflow can be organized.",
                    )
                  }
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  查詢類似導入方式
                </Button>
                <Link
                  to="/erp/case-studies"
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-slate-700 transition hover:border-slate-500 hover:bg-slate-50"
                >
                  返回案例中心
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-[#fbfaf7] p-6">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                Case Snapshot
              </div>
              <div className="mt-5 grid gap-3">
                {[
                  ["行業", "旅遊巴、包車、車隊服務"],
                  ["常見客戶", "學校、旅行社、企業、活動主辦方、熟客預存款客戶"],
                  ["核心流程", "報價、查詢可用車輛、確認訂單、派車、完成服務、發票、預存款扣數"],
                  ["第一階段重點", "把接單查車、派車安排、月結和熟客扣數接成同一張訂單脈絡"],
                  ["適合公司", "已有穩定包車流程，但資料仍靠 Excel 和訊息交接"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-slate-200 bg-white p-4"
                  >
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">
                      {label}
                    </div>
                    <div className="mt-2 font-semibold leading-7 text-slate-900">
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f7f5ef] py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <span className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-600">
                  Real Operation Pain
                </span>
                <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                  為什麼包車公司會開始需要 ERP？
                </h2>
                <p className="mt-4 leading-8 text-slate-600">
                  當公司接單量不大時，用 Excel 和訊息交接仍然可以勉強運作。
                  但當月結客戶增加、幾個銷售同時接單、車隊安排更密，甚至有熟客先入錢再慢慢扣數，資料分散就會直接影響報價速度、可用車輛判斷、排車和客戶餘額核對。
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {painPoints.map((pain) => (
                  <div
                    key={pain}
                    className="rounded-lg border border-slate-200 bg-white p-5"
                  >
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-line mt-1 text-xl text-orange-600"></i>
                      <p className="leading-7 text-slate-700">{pain}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-700">
                Workflow Design
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                建議把流程拆成 5 段整理
              </h2>
              <p className="mt-4 leading-8 text-slate-600">
                第一階段不需要把所有車隊細節一次過做完。更穩妥的做法，是先把「客戶查詢什麼服務、當日是否有車可用、公司安排了什麼資源、服務是否完成、應收或預存款應扣多少」接起來。
              </p>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-2">
              {workflowSteps.map((item) => (
                <article
                  key={item.step}
                  className="rounded-lg border border-slate-200 bg-[#fbfaf7] p-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-950 text-sm font-bold text-white">
                      {item.step}
                    </div>
                    <h3 className="text-2xl font-bold text-slate-950">
                      {item.title}
                    </h3>
                  </div>
                  <div className="mt-5 grid gap-3">
                    <div className="rounded-lg border border-rose-100 bg-white p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-rose-600">
                        Before
                      </div>
                      <p className="mt-2 leading-7 text-slate-600">
                        {item.before}
                      </p>
                    </div>
                    <div className="rounded-lg border border-teal-100 bg-white p-4">
                      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">
                        ERP Flow
                      </div>
                      <p className="mt-2 leading-7 text-slate-600">
                        {item.erp}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-16 text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[0.88fr_1.12fr]">
              <div>
                <span className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-200">
                  ERP Modules
                </span>
                <h2 className="mt-3 text-3xl font-bold md:text-5xl">
                  這類公司常用到的 ERP 模組
                </h2>
                <p className="mt-4 leading-8 text-slate-300">
                  模組不是越多越好。包車公司第一階段最重要是讓銷售接查詢時看得到當日訂單和車輛佔用情況，再讓訂單資料由業務流到車務和財務。
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {erpModules.map((module) => (
                  <div
                    key={module.title}
                    className="rounded-lg border border-white/10 bg-white/5 p-5"
                  >
                    <h3 className="text-xl font-bold text-white">
                      {module.title}
                    </h3>
                    <p className="mt-3 text-sm leading-7 text-slate-300">
                      {module.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#f7f5ef] py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-700">
                Expected Outcomes
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                導入後管理上會先看見什麼改善？
              </h2>
              <p className="mt-4 leading-8 text-slate-600">
                這類 ERP 導入的成效，通常不是第一天就變成大型系統，而是前線、車務和財務開始使用同一套訂單資料，令每日交接、月底對數和熟客餘額扣數變得可追蹤。
              </p>
            </div>
            <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {outcomes.map((item) => (
                <article
                  key={item.metric}
                  className="rounded-lg border border-slate-200 bg-white p-6"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-teal-600 text-xl text-white">
                    <i className="ri-checkbox-circle-line"></i>
                  </div>
                  <h3 className="mt-4 text-xl font-bold text-slate-950">
                    {item.metric}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.detail}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-600">
                FAQ
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                旅遊巴 / 包車公司 ERP 常見問題
              </h2>
            </div>
            <div className="mt-10 space-y-4">
              {faqItems.map((item) => (
                <article
                  key={item.q}
                  className="rounded-lg border border-slate-200 bg-[#fbfaf7] p-6"
                >
                  <h3 className="text-xl font-bold leading-8 text-slate-950">
                    {item.q}
                  </h3>
                  <p className="mt-3 leading-8 text-slate-600">{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-950 py-16 text-white">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <div className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-200">
              AutoERP
            </div>
            <h2 className="mt-4 text-3xl font-bold leading-tight md:text-5xl">
              如果你的包車流程仍然靠 Excel 和訊息交接，
              <span className="block text-teal-200">
                可以先由報價、查車到熟客扣數開始整理
              </span>
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              我們會先了解你公司現有 SOP，再建議第一階段應導入哪些 ERP 範圍，讓銷售、車務和財務先用同一套資料合作。
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                variant="primary"
                size="lg"
                className="px-8 py-4 text-base"
                onClick={() =>
                  openWhatsApp(
                    "您好！我們是旅遊巴 / 包車公司，想了解 AutoERP 可以怎樣整理報價、派車、月結和熟客扣數流程。",
                    "Hello! We are a tour bus / charter service company and would like to understand how AutoERP can organize quotation, dispatch, monthly billing and customer balance deduction workflows.",
                  )
                }
              >
                <i className="ri-whatsapp-line mr-2"></i>
                查詢旅遊巴 ERP 導入
              </Button>
              <Link
                to="/erp"
                className="inline-flex items-center justify-center rounded-lg bg-white px-8 py-4 text-base font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                查看 ERP 方案
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
